import { Box, Skeleton } from '@mui/material';
import { memo } from 'react';

/** Matches {@link AddCatalogItemWithQty} primary action (contained `size="small"`, height 32px). */
const ADD_BUTTON_SKELETON_WIDTH = 65;
const ADD_BUTTON_SKELETON_HEIGHT = 32;

/** Matches {@link CatalogItemCheckbox} icon (`28px` × `28px`). */
const CHECKBOX_SKELETON_SIZE = 28;

export type CatalogItemSkeletonActionVariant = 'addToOrder' | 'propertyCatalog';

export interface CatalogItemSkeletonProps {
    actionVariant?: CatalogItemSkeletonActionVariant;
}

export const CatalogItemSkeleton = memo(
    ({ actionVariant = 'propertyCatalog' }: CatalogItemSkeletonProps) => {
        const isCatalogRow = actionVariant === 'propertyCatalog';

        return (
            <Box
                sx={{
                    borderBottom: '1px solid',
                    borderColor: 'grey.500',
                }}
            >
                <Box
                    sx={{
                        padding: '20px 0',
                        minHeight: '140px',
                        boxSizing: 'border-box',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'flex-start',
                            gap: '16px',
                        }}
                    >
                        {isCatalogRow ? (
                            <Box
                                sx={{
                                    flexShrink: 0,
                                    paddingTop: '4px',
                                }}
                            >
                                <Skeleton
                                    variant="rounded"
                                    width={CHECKBOX_SKELETON_SIZE}
                                    height={CHECKBOX_SKELETON_SIZE}
                                    sx={{ flexShrink: 0 }}
                                />
                            </Box>
                        ) : null}
                        <Skeleton
                            variant="rounded"
                            width={100}
                            height={100}
                            sx={{ flexShrink: 0 }}
                        />
                        <Box
                            sx={{
                                display: 'flex',
                                flexGrow: 1,
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                minHeight: '100px',
                                minWidth: 0,
                                gap: '12px',
                            }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        marginBottom: '4px',
                                    }}
                                >
                                    {isCatalogRow ? (
                                        <Skeleton
                                            height="12px"
                                            width="56px"
                                            sx={{
                                                flexShrink: 0,
                                                marginBottom: 0,
                                            }}
                                        />
                                    ) : null}
                                    <Skeleton
                                        height="15px"
                                        width={isCatalogRow ? '70%' : '92%'}
                                        sx={{ marginBottom: 0, minWidth: 0 }}
                                    />
                                </Box>
                                <Skeleton
                                    height="13px"
                                    width="36%"
                                    sx={{ marginTop: '2px' }}
                                />
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '6px',
                                    flexShrink: 0,
                                }}
                            >
                                {isCatalogRow ? (
                                    <Skeleton height="12px" width="72px" />
                                ) : null}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                    }}
                                >
                                    <Skeleton
                                        variant="circular"
                                        width={18}
                                        height={18}
                                    />
                                    <Skeleton height="13px" width="44%" />
                                </Box>
                            </Box>
                        </Box>
                        {!isCatalogRow ? (
                            <Box
                                sx={{
                                    minWidth: '110px',
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    alignItems: 'flex-start',
                                    flexShrink: 0,
                                }}
                            >
                                <Skeleton
                                    variant="rounded"
                                    width={ADD_BUTTON_SKELETON_WIDTH}
                                    height={ADD_BUTTON_SKELETON_HEIGHT}
                                    sx={{ borderRadius: '4px', flexShrink: 0 }}
                                />
                            </Box>
                        ) : null}
                    </Box>
                </Box>
            </Box>
        );
    },
);
